import React, { useCallback, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import { SecondaryButton } from "components/Form";
import { Column, Row } from "components/Layout";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { SmallText } from "components/Text";

import { AgGridTables } from "constants/aggrid";
import {
  SideLoadedBusinessColId,
  SideLoadedBusinessColumnDef,
} from "constants/columnDefinitions/sideLoadedBusiness";

import {
  getBusinesses,
  getSideLoadedBusinesses,
  searchSideLoadedBusinesses,
} from "selectors";
import { useDebounce } from "@react-hook/debounce";
import SearchInput from "components/SearchInput";
import { SideLoadedBusinessSearchResult } from "selectors/sideLoadedBusinesses";

interface SideLoadedBusinessTableProps {
  initialSearchTerm?: string;
  onClose: () => void;
  onSelect: (recordId: string) => void;
}

const columnDefs = [
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.NAME],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.ABN],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.USED],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.ACTIONS],
];

const getRowId = (params: { data: { id: string } }) => params.data.id;

const SideLoadedBusinessTable = ({
  initialSearchTerm = "",
  onClose,
  onSelect,
}: SideLoadedBusinessTableProps): React.JSX.Element => {
  // Use our Fuse search results, rather than the embedded AgGrid one in order to give
  // consistent results.
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    300,
  );
  React.useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [setSearchTerm, debouncedSearchTerm]);
  React.useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [setDebouncedSearchTerm, searchTerm]);

  const sideLoadedBusinesses = useSelector(getSideLoadedBusinesses);
  const businessesById = useSelector(getBusinesses);

  const sideLoadResults: SideLoadedBusinessSearchResult[] = useSelector(
    searchSideLoadedBusinesses(debouncedSearchTerm),
  );
  const sideLoadIds = useMemo(
    () =>
      debouncedSearchTerm
        ? sideLoadResults.map(result => result.item.id)
        : null,
    [sideLoadResults, debouncedSearchTerm],
  );

  // Show all results when there is no search term.
  const rowData = useMemo(() => {
    if (sideLoadIds) {
      return Object.values(sideLoadedBusinesses).filter(
        (business: SideLoadedBusiness) => sideLoadIds.includes(business.id),
      );
    }
    return Object.values(sideLoadedBusinesses);
  }, [sideLoadedBusinesses, sideLoadIds]);

  const handleSelectAndCompare = useCallback(
    (selectedRecordId: string) => {
      if (selectedRecordId) {
        onSelect(selectedRecordId);
      }
    },
    [onSelect],
  );

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle onClose={onClose}>Data Suggestions</DialogTitle>
      <DialogContent dividers>
        <Column full>
          <Row paddingVertical={1}>
            <SmallText>
              We found data that matches what you entered. You can use any of
              these sources to improve your Business data. Suggestions are
              sourced from your Business list or files you can upload which
              contain business records.
            </SmallText>
          </Row>
          <Row fullWidth>
            <SearchInput
              value={searchTerm}
              handleChange={setSearchTerm}
              style={{ marginLeft: 0 }}
            />
          </Row>
          <AgGridTable
            columnDefs={columnDefs}
            rowData={rowData}
            getRowId={getRowId}
            hasExportCsvPermissions={false}
            tableName={AgGridTables.SIDELOADED_BUSINESSES}
            WrapperComponent={ModalTableWrapper}
            showGlobalSearchFilter={false}
            hideSavedViews
            context={{
              handleSelectAndCompare,
              businessesById,
            }}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
      </DialogActions>
    </ZoomyDialog>
  );
};

export default SideLoadedBusinessTable;
